import React, { useSyncExternalStore, useCallback, useState, useEffect } from 'react'
import type { CachedData } from './services/cache/types'
import { useAppContext } from '~/app-context'
import type { DocumentTransactionAssociation } from '~/storage/types'

const EMPTY_ARRAY: string[] = []
const EMPTY_ASSOC_ARRAY: DocumentTransactionAssociation[] = []

function getMatchAssociationsFromCache(
    cache: { getKey: (k: keyof CachedData) => unknown },
    transactionId: string,
): DocumentTransactionAssociation[] {
    if (!transactionId) return EMPTY_ASSOC_ARRAY
    const matchesByTx = (cache.getKey('matchesByTransaction') ?? {}) as Record<
        string,
        string[]
    >
    const assocIds = matchesByTx[transactionId] ?? []
    const assocs = (cache.getKey('documentTransactionAssociations') ?? {}) as Record<
        string,
        DocumentTransactionAssociation
    >
    return assocIds
        .map((id) => assocs[id])
        .filter((a): a is DocumentTransactionAssociation => a != null)
}

/**
 * Returns the value of the specified key in the cache for use in React components (re-renders on change).
 * @param key The key to get the value of.
 * @returns The value of the key.
 */
export function useCacheValue<Key extends keyof CachedData>(
    key: Key,
): CachedData[Key] {
    const cache = useAppContext().services.cache
    return useSyncExternalStore(
        useCallback(
            (onStoreChange) =>
                cache.onUpdate.listen((updatedKeys) => {
                    if (updatedKeys.has(key)) {
                        onStoreChange()
                    }
                }),
            [cache, key],
        ),
        () => cache.getKey(key),
    )
}

/**
 * Subscribe to changes for a single email by id.
 * Re-renders only when that id is updated in cache.
 */
export function useEmailById(id: string) {
    const cache = useAppContext().services.cache
    const subscribe = useCallback(
        (onStoreChange) =>
            cache.onEmailsUpdate.listen((changedIds) => {
                if (changedIds.has(id)) {
                    onStoreChange()
                }
            }),
        [cache, id],
    )
    return useSyncExternalStore(subscribe, () => cache.getKey('emails')[id])
}

/**
 * Subscribe to membership changes of email IDs only (add/remove), not content changes.
 * This prevents list-wide re-renders when existing emails update.
 */
export function useEmailIds(): string[] {
    const cache = useAppContext().services.cache
    return useSyncExternalStore(
        useCallback(
            (onStoreChange) =>
                cache.onEmailIdsUpdate.listen(() => onStoreChange()),
            [cache],
        ),
        () => cache.getEmailIds(),
    )
}

/**
 * Subscribe to invoice IDs for a specific email. Re-renders only when that email's
 * invoice mapping changes.
 */
export function useInvoiceIdsByEmailId(emailId: string): string[] {
    const cache = useAppContext().services.cache
    const subscribe = useCallback(
        (onStoreChange) =>
            cache.onEmailInvoicesUpdate?.listen((changedEmailIds) => {
                if (changedEmailIds.has(emailId)) {
                    onStoreChange()
                }
            }) ?? (() => () => {}),
        [cache, emailId],
    )
    return useSyncExternalStore(subscribe, () => {
        const ids = cache.getKey('emailInvoices')[emailId]
        return ids ?? EMPTY_ARRAY // Stable reference
    })
}

/**
 * Subscribe to changes for a single invoice by id.
 * Re-renders only when that id is updated in cache.
 */
export function useInvoiceById(id: string) {
    const cache = useAppContext().services.cache
    const subscribe = useCallback(
        (onStoreChange) =>
            cache.onInvoicesUpdate.listen((changedIds) => {
                if (changedIds.has(id)) {
                    onStoreChange()
                }
            }),
        [cache, id],
    )
    return useSyncExternalStore(subscribe, () => cache.getKey('invoices')[id])
}

/**
 * Subscribe to changes for a single transaction by id.
 * Re-renders only when that id is updated in cache.
 */
export function useTransactionById(id: string) {
    const cache = useAppContext().services.cache
    const subscribe = useCallback(
        (onStoreChange) =>
            cache.onTransactionsUpdate.listen((changedIds) => {
                if (changedIds.has(id)) {
                    onStoreChange()
                }
            }),
        [cache, id],
    )
    return useSyncExternalStore(
        subscribe,
        () => cache.getKey('transactions')?.[id],
    )
}

/**
 * Subscribe to match associations for a single transaction.
 * Re-renders only when that transaction's associations change.
 */
export function useMatchAssociationsForTransaction(
    transactionId: string,
): DocumentTransactionAssociation[] {
    const cache = useAppContext().services.cache
    const [associations, setAssociations] = useState<
        DocumentTransactionAssociation[]
    >(() => getMatchAssociationsFromCache(cache, transactionId))

    useEffect(() => {
        if (!transactionId) return
        const unsubscribe =
            cache.onTransactionsWithAssociationChanges.listen((changedIds) => {
                if (changedIds.has(transactionId)) {
                    setAssociations(
                        getMatchAssociationsFromCache(cache, transactionId),
                    )
                }
            })
        return unsubscribe
    }, [cache, transactionId])

    return associations
}
